///
/// EarthBrowser - Vite Entry Point
///
/// Created: 2025
/// Copyright (c) 2025 Geodata Labs
///

import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './app/globals.css'
import { initPostHog } from './app/instrumentation-client'
import { initSentry, Sentry } from './app/sentry'
import { crashTrail, crashVerdict, initCrashSentinel, lastSessionCrashed } from './earthbrowser/utils/CrashSentinel'
import { startMemoryWatch } from './earthbrowser/utils/MemoryWatch'
import { hideSplash, initNative } from './native'
import { installReduxEngineBridge } from './redux/engineBridgeSetup'
import { initAnalytics, track } from './utils/analytics'
import { isFactoryResetPending, runPendingFactoryReset } from './utils/factoryReset'
import { initializeTrackingConsent } from './utils/privacy-consent'

const root = document.getElementById('root')!

async function boot() {
	// A pending factory reset must run before anything renders: once the app
	// mounts, SQLite workers hold OPFS file locks that block deletion
	if (isFactoryResetPending()) {
		root.textContent = 'Resetting EarthBrowser…'
		const resetting = await runPendingFactoryReset()
		if (resetting) {
			return // page reloads into a fresh state
		}
		root.textContent = ''
	}

	// Capacitor shell setup (status bar, deep links, back button); no-op on
	// the web. Before the sentinel so a native crash verdict and listeners
	// are both in place for the first frame.
	await initNative()

	// Detect whether the last session crashed (memory jettison on mobile has
	// no event - only the absence of a clean shutdown). Must run before the
	// store rehydrates so MemoryBudget and StoreProvider see the verdict.
	initCrashSentinel()

	// Initialize Sentry and PostHog before rendering, then apply the stored
	// consent choice to both live clients (opt in/out is a no-op on an
	// uninitialized SDK). First-party metrics (Tier 1) start here too.
	initSentry()
	initPostHog()
	initializeTrackingConsent()
	initAnalytics()

	// Report the crashed session's breadcrumb trail (recorded via
	// recordCrumb, recovered by the sentinel). The last crumb brackets the
	// allocation that killed the tab - the crash itself produces no logs.
	if (lastSessionCrashed()) {
		const trail = crashTrail()
		const verdict = crashVerdict()
		const text = trail?.length ? trail.join('\n') : '(no breadcrumb trail recorded)'
		console.warn(`[CrashSentinel] Breadcrumbs from the crashed session:\n${text}`)
		Sentry.captureMessage('Previous session crashed (tab killed without clean shutdown)', {
			level: 'warning',
			extra: { trail, verdict },
		})
		track('session_crashed', {
			crumbs: (trail ?? []).slice(-3),
			level: verdict.level,
			suspect: verdict.suspect ?? undefined,
			layers: verdict.heavyLayers,
			same_tab: verdict.sameTab,
			reason: verdict.reason ?? undefined,
		})
	}

	// Wire the engine's redux seam to the live store before anything
	// constructs engine objects (Clock, EarthActivity, camera state)
	installReduxEngineBridge()

	// Chromium only: sample page memory and go lite before the OS kills
	// the tab (no-op where measureUserAgentSpecificMemory is missing)
	startMemoryWatch()

	ReactDOM.createRoot(root).render(
		<App />
	)

	// Native splash stays up (launchAutoHide: false) until the app has
	// painted; no-op on the web
	hideSplash()
}

void boot()
